import { BaseModel, ModelInstance, ToFrontHook, ToBackHook, DateField } from './util';
import { TournamentTeamUser } from './tournament-team-user';
import { Team } from './team';
import { TeamUser } from './team-user';
import { User } from './user';
import { TournamentTeam } from './tournament-team';
import { BaseStatistic, WinLoseType } from './base-statistic';
import { LeaguePlayer } from './league-player';

@ModelInstance({
  mappingFields: {
    league_player: 'leaguePlayer',
    tournament_team_user: 'tournamentTeamUser',
    team: 'team',
    team_user: 'teamUser',
    user: 'user',
    tournament_team: 'tournamentTeam',
    month: 'month',
    win_lose: 'winLose',
    games_count: 'gamesCount',
    won_games_count: 'wonGamesCount',
    lose_games_count: 'loseGamesCount',
    draw_games_count: 'drawGamesCount',
    team_score_sum: 'teamScoreSum',
    team_missed_sum: 'teamMissedSum',
    tournament_points: 'tournamentPoints',
    points: 'points',
    shot_misses: 'shotMisses',
    shots_on_goal: 'shotsOnGoal',
    shots_blocked: 'shotsBlocked',
    shots_on_goal_percent: 'shotsOnGoalPercent',
    shots: 'shots',
    goals: 'goals',
    goals_percent: 'goalsPercent',
    total_goals: 'totalGoals',
    assists: 'assists',
    penalty_goals: 'penaltyGoals',
    penalty_attempts: 'penaltyAttempts',
    penalty_goals_percent: 'penaltyGoalsPercent',
    penalty_saves: 'penaltySaves',
    penalty_goals_against: 'penaltyGoalsAgainst',
    penalty_shots_against: 'penaltyShotsAgainst',
    penalty_saves_percent: 'penaltySavesPercent',
    small_penalty_goals: 'smallPenaltyGoals',
    small_penalty_attempts: 'smallPenaltyAttempts',
    small_penalty_goals_percent: 'smallPenaltyGoalsPercent',
    small_penalty_saves: 'smallPenaltySaves',
    small_penalty_goals_against: 'smallPenaltyGoalsAgainst',
    small_penalty_shots_against: 'smallPenaltyShotsAgainst',
    small_penalty_saves_percent: 'smallPenaltySavesPercent',
    saves: 'saves',
    saves_percent: 'savesPercent',
    total_saves: 'totalSaves',
    goals_against: 'goalsAgainst',
    total_goals_against: 'totalGoalsAgainst',
    shots_against: 'shotsAgainst',
    safety_rate: 'safetyRate',
    fouls: 'fouls',
    yellow_cards: 'yellowCards',
    red_cards: 'redCards',
    perfect_passes: 'perfectPasses',
    losses: 'losses',
    steals: 'steals',
    outs: 'outs',
    block_shots: 'blockShots',
    corners: 'corners',
    free_kicks: 'freeKicks',
    game_time: 'gameTime',
    plus_minus: 'plusMinus',
    newbie: 'newbie',
    rank: 'rank',
    auto_goals: 'autoGoals',
    auto_goals_against: 'autoGoalsAgainst',
    end_game_penalty_goals: 'endGamePenaltyGoals',
    end_game_penalty_misses: 'endGamePenaltyMisses',
    end_game_penalty_saves: 'endGamePenaltySaves',
    shutout: 'shutout',
    shutout_percent: 'shutoutPercent',
    duels_won: 'duelsWon',
    duels_lost: 'duelsLost',
    double_goals: 'doubleGoals',
    double_goals_against: 'doubleGoalsAgainst',
  },
  relation: {
    leaguePlayer: LeaguePlayer,
    tournamentTeamUser: TournamentTeamUser,
    team: Team,
    teamUser: TeamUser,
    user: User,
    tournamentTeam: TournamentTeam,
    month: DateField
  }
})
export class FootballStatistic extends BaseModel implements BaseStatistic {
  leaguePlayer: LeaguePlayer;
  tournamentTeamUser: TournamentTeamUser;
  team: Team;
  teamUser: TeamUser;
  user: User;
  tournamentTeam: TournamentTeam;
  month: Date;
  winLose: WinLoseType;
  gamesCount: number;
  wonGamesCount: number;
  loseGamesCount: number;
  drawGamesCount: number;
  teamScoreSum: number;
  teamMissedSum: number;
  tournamentPoints: number;
  points: number;
  shotMisses: number;
  shotsOnGoal: number;
  shotsBlocked: number;
  shotsOnGoalPercent: number;
  shots: number;
  goals: number;
  totalGoals: number;
  goalsPercent: number;
  assists: number;
  penaltyGoals: number;
  penaltyAttempts: number;
  penaltyGoalsPercent: number;
  penaltySaves: number;
  penaltyGoalsAgainst: number;
  penaltyShotsAgainst: number;
  penaltySavesPercent: number;
  smallPenaltyGoals: number;
  smallPenaltyAttempts: number;
  smallPenaltyGoalsPercent: number;
  smallPenaltySaves: number;
  smallPenaltyGoalsAgainst: number;
  smallPenaltyShotsAgainst: number;
  smallPenaltySavesPercent: number;
  saves: number;
  savesPercent: number;
  totalSaves: number;
  goalsAgainst: number;
  totalGoalsAgainst: number;
  shotsAgainst: number;
  safetyRate: number;
  fouls: number;
  yellowCards: number;
  redCards: number;
  perfectPasses: number;
  losses: number;
  steals: number;
  outs: number;
  gameTime: number;
  plusMinus: number;
  blockShots: number;
  corners: number;
  freeKicks: number;
  newbie: boolean;
  rank: number;
  autoGoals: number;
  autoGoalsAgainst: number;
  endGamePenaltyGoals: number;
  endGamePenaltyMisses: number;
  endGamePenaltySaves: number;
  shutout: number;
  shutoutPercent: number;
  duelsWon: number;
  duelsLost: number;
  doubleGoals: number;
  doubleGoalsAgainst: number;

  get teamPlusMinus(): number {
    return (this.teamScoreSum || 0) - (this.teamMissedSum || 0);
  }

  get userMinutes(): string {
    if (!this.gameTime) {
      return '00:00';
    }
    const minutes = Math.floor(this.gameTime / 60);
    const seconds = Math.floor(this.gameTime - minutes * 60);
    return `${minutes < 10 ? 0 : ''}${minutes}:${seconds < 10 ? 0 : ''}${seconds}`;
  }

  get penalties(): number {
    return Math.floor(10 * ((this.penaltyGoals || 0) + (this.penaltyAttempts || 0))) / 10;
  }

  get smallPenalties(): number {
    return Math.floor(10 * ((this.smallPenaltyGoals || 0) + (this.smallPenaltyAttempts || 0))) / 10;
  }

  get goalpass(): number {
    return Math.floor(10 * ((this.totalGoals || 0) + (this.assists || 0))) / 10;
  }

  get tournamentTeamPoints(): number {
    return this.tournamentTeam && this.tournamentTeam.points;
  }

  get tournamentTeamGamesCount(): number {
    return this.tournamentTeam && this.tournamentTeam.gamesCount;
  }

  get tournamentTeamWonGamesCount(): number {
    return this.tournamentTeam && this.tournamentTeam.wonGamesCount;
  }

  get tournamentTeamDrawGamesCount(): number {
    return this.tournamentTeam && this.tournamentTeam.drawGamesCount;
  }

  get tournamentTeamLostGamesCount(): number {
    if (!this.tournamentTeam) {
      return null;
    }
    return this.tournamentTeam.gamesCount - this.tournamentTeam.drawGamesCount - this.tournamentTeam.wonGamesCount;
  }

  get tournamentTeamScoreSum(): number {
    return this.tournamentTeam && this.tournamentTeam.scoreSum;
  }

  get tournamentTeamMissedSum(): number {
    return this.tournamentTeam && this.tournamentTeam.missedSum;
  }

  get tournamentTeamPlusMinus(): number {
    if (!this.tournamentTeam) {
      return null;
    }
    return this.tournamentTeam.scoreSum - this.tournamentTeam.missedSum;
  }

  @ToFrontHook
  static toFront(data: any): any {}

  @ToBackHook
  static toBack(data: any): any {}
}
